import React, {useEffect, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import CustomButton from '../CustomButton';
import {useFormInput} from '../../utils/utilities';
import {useDispatch, useSelector} from 'react-redux';
import {
  addElevator,
  editElevator,
  getBuildings,
  getEntrancesByBuilding,
} from '../../actions';
import Select from '../Select';
import {fieldError, isFieldIvalid} from '../../utils/errorHandler';
import {getAllBuildings} from '../../actions/buildingsActions';

const ElevatorModal = ({elevatorToEdit, configureElevator}) => {
  const dispatch = useDispatch();
  const {modalProps} = useSelector(s => s.modal);
  const {elevatorsLoading, elevatorsError} = useSelector(s => s.elevators);
  const {allBuildings} = useSelector(s => s.buildings);
  const {buildingEntrances, entrancesLoading} = useSelector(s => s.entrances);
  const name = useFormInput(elevatorToEdit?.name || '');
  const imei = useFormInput(elevatorToEdit?.imei || '');
  const phoneNumber = useFormInput(elevatorToEdit?.phoneNumber || '');
  const elevatorId = useFormInput(elevatorToEdit?.elevator_id || '');
  const [building, setBuilding] = useState(
    elevatorToEdit
      ? elevatorToEdit.buildingId
      : modalProps.building
      ? modalProps.building.id
      : '',
  );
  const [entrance, setEntrance] = useState(
    modalProps.entrance ? modalProps.entrance.id : '',
  );

  const [category, setCategory] = useState(
    elevatorToEdit?.category || ['Lift'],
  );

  const onSubmit = e => {
    e.preventDefault();
    const values = [
      name.value,
      imei.value,
      entrance,
      phoneNumber.value,
      building,
      category[0],
      elevatorId.value,
    ];
    if (elevatorToEdit) {
      dispatch(editElevator(...values, elevatorToEdit.id));
    } else {
      dispatch(addElevator(...values));
    }
  };

  const onChangeBuilding = value => {
    setBuilding(value);
    setEntrance('');
  };

  useEffect(() => {
    if (building) {
      dispatch(getEntrancesByBuilding(building));
    }
  }, [building]);

  const onChangeEntrance = value => {
    setEntrance(value);
  };

  useEffect(() => {
    if (elevatorToEdit) {
      dispatch(getEntrancesByBuilding(elevatorToEdit.buildingId));
      setBuilding(elevatorToEdit.buildingId);
      setEntrance(elevatorToEdit.entranceId);
      name.manuallyChange(elevatorToEdit.name);
      imei.manuallyChange(elevatorToEdit.imei);
      phoneNumber.manuallyChange(elevatorToEdit.phoneNumber);
      setCategory(elevatorToEdit.category || ['Lift']);
      elevatorId.manuallyChange(elevatorToEdit.elevatorId);
    }
  }, [elevatorToEdit]);

  useEffect(() => {
    dispatch(getAllBuildings());
  }, []);

  return (
    <>
      <Typography alignSelf="center" variant="h6" mt="1rem" fontWeight="bold">
        {elevatorToEdit ? 'Edit elevator' : 'Add a new elevator'}
      </Typography>
      <form onSubmit={onSubmit}>
        <Box display="flex" justifyContent="center">
          <Box
            display="flex"
            flexDirection="column"
            mt="2rem"
            gap="1rem"
            sx={{
              width: {
                xs: 'calc(100vw - 4rem)',
                sm: elevatorToEdit ? '70%' : '30rem',
              },
            }}>
            <TextField
              {...name}
              error={isFieldIvalid(elevatorsError, 'name')}
              helperText={fieldError(elevatorsError, 'name')}
              label="Elevator name"
            />
            <Select
              items={allBuildings}
              value={building}
              onChangeValue={onChangeBuilding}
              label="Building"
              error={isFieldIvalid(elevatorsError, 'buildingId')}
              helperText={fieldError(elevatorsError, 'buildingId')}
            />
            <Select
              items={buildingEntrances}
              value={entrance}
              onChangeValue={onChangeEntrance}
              label="Entrance"
              disabled={
                !building || entrancesLoading || buildingEntrances.length === 0
              }
              error={isFieldIvalid(elevatorsError, 'entranceId')}
              helperText={fieldError(elevatorsError, 'entranceId')}
            />
            <TextField
              {...imei}
              error={isFieldIvalid(elevatorsError, 'imei')}
              helperText={fieldError(elevatorsError, 'imei')}
              required
              label="IMEI"
            />
            <TextField
              {...phoneNumber}
              error={isFieldIvalid(elevatorsError, 'phoneNumber')}
              helperText={fieldError(elevatorsError, 'phoneNumber')}
              label="Phone number"
            />
            <Select
              items={[
                {id: 'Lift', name: 'Lift'},
                {id: 'Stairs', name: 'Stairs'},
              ]}
              value={category}
              onChangeValue={setCategory}
              label="Category"
              error={isFieldIvalid(elevatorsError, 'category')}
              helperText={fieldError(elevatorsError, 'category')}
            />
            <TextField
              {...elevatorId}
              error={isFieldIvalid(elevatorsError, 'elevator_id')}
              helperText={fieldError(elevatorsError, 'elevator_id')}
              label="Elevator ID"
            />
            <CustomButton loading={elevatorsLoading} type="submit">
              {elevatorToEdit ? 'Save' : 'Submit'}
            </CustomButton>
          </Box>
        </Box>
      </form>
      {elevatorToEdit && (
        <Box mt="1rem" display="flex" justifyContent="center">
          <CustomButton
            onClick={configureElevator}
            color="secondary"
            sx={{width: {xs: 'calc(100vw - 4rem)', sm: '70%'}}}>
            Edit configuration
          </CustomButton>
        </Box>
      )}
    </>
  );
};

export default ElevatorModal;
